import { inject, Injectable } from '@angular/core';
import { EntityState } from '@ngrx/entity';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { concatMap, map, Observable, of } from 'rxjs';

import {
  CanvasElement,
  ElementTargetPath,
  getRootElementId,
  PartialPropsOf,
} from '@blaze-runtime/engine-domain';
import { PageInfo, PageType } from '@blaze-shared/api-contracts-domain';
import { NotificationService } from '@blaze-shared/platform-data-access';
import { blazePageActions, PagesApiService } from '@blaze-shared/projects-data-access';
import { StyleSheet } from '@blaze-shared/theming-domain';

import { fromElements } from '../+state';
import { getLayoutIds } from '../+state/elements/elements.selectors';
import { hasProps } from '../canvas-elements/element-model/element-props-guards';
import {
  isElementType,
  isVariantOfFormContainer,
} from '../canvas-elements/element-model/element-type-guards';
import { LayoutsApiDataResponse, LayoutsApiService } from '../data/layouts-api.service';
import {
  FORM_PADDING_DRAGGED,
  FORM_PADDING_GENERATED,
  FormContainerPropsModel,
} from '../elements/form-container/form-container.model';
import { elementsToEntityState, entityStateToElements } from '../utils/elements/element.utils';
import { getPathLastInSlot } from '../utils/layout/helpers';
import { placeElementMutably } from '../utils/layout/place-element';
import { removeElementFromParentMutably } from '../utils/layout/remove-element';

import { LayoutGenerationService } from './layout-generation.service';
import { LayoutLoadingService } from './layout-loading.service';
import { PageGenerationService } from './page-pregeneration.service';

export interface PageConversionResult {
  actions: Action[];
  page: PageInfo;
}

@Injectable({ providedIn: 'root' })
export class PageConversionService {
  private readonly layoutGenerationService = inject(LayoutGenerationService);
  private readonly layoutLoadingService = inject(LayoutLoadingService);
  private readonly layoutsApi = inject(LayoutsApiService);
  private readonly notificationService = inject(NotificationService);
  private readonly pageGenerationService = inject(PageGenerationService);
  private readonly pagesApi = inject(PagesApiService);
  private readonly store = inject(Store);

  convertToTargetType(
    page: PageInfo,
    targetType: PageType,
    styleSheets: StyleSheet[],
  ): Observable<PageConversionResult> {
    if (targetType === 'Dialog') {
      return this.convertToDialog(page, styleSheets);
    } else if (targetType === 'Drawer') {
      return this.convertToDrawer(page, styleSheets);
    } else if (targetType === 'Page') {
      return this.convertToPage(page);
    }
    throw new Error('unreachable code reached');
  }

  convertToDrawer(page: PageInfo, styleSheets: StyleSheet[]): Observable<PageConversionResult> {
    if (!page.id) {
      throw new Error('Convert to drawer failed: unknown page');
    }
    if (page.pageType === 'Drawer') {
      throw new Error(`The page ${page.id} is already a Drawer.`);
    }

    return this.layoutLoadingService.getLayout(page.id).pipe(
      concatLatestFrom(() =>
        this.store.select(getLayoutIds).pipe(map((ids) => ids.includes(page.id as string))),
      ),
      concatMap(([layout, isLoaded]) => {
        const rootId = getRootElementId(layout.pageId);
        const entitiesState = elementsToEntityState(layout.uiLayer.content);

        const rootElement = entitiesState.entities[rootId];
        if (!rootElement) {
          throw new Error('Missing root element');
        }
        const mainChildren = isElementType(rootElement, 'root')
          ? [...(rootElement.slots.main?.children || [])]
          : isElementType(rootElement, 'dialog')
            ? [...(rootElement.slots.content?.children || [])]
            : [];

        this.convertDialogTypeMutably(
          'drawer',
          mainChildren,
          layout,
          rootElement,
          entitiesState,
          styleSheets,
        );

        return this.layoutsApi.updateLayouts([layout]).pipe(
          concatMap((layouts) =>
            this.pagesApi
              .updatePage(page.id, {
                name: page.name,
                pageType: 'Drawer',
                isHomePage: false,
                mainDatasetId: page.mainDatasetId,
                urlSlug: page.urlSlug,
              })
              .pipe(
                concatMap((dialog) => {
                  const resultActions: Action[] = [
                    fromElements.updateElementsActionPage({
                      pageId: page.id as string,
                      patch: { pageType: 'Drawer' },
                    }),
                  ];
                  if (isLoaded) {
                    const elements = layouts[0].uiLayer.content;
                    resultActions.push(fromElements.layoutsSaved({ layouts }));
                    resultActions.push(
                      fromElements.replaceElements({ ns: dialog.id as string, elements }),
                    );
                  }
                  resultActions.push(blazePageActions.convertToDrawerSuccess({ updated: dialog }));
                  this.notificationService.success('Conversion complete');
                  return of({ actions: resultActions, page: dialog });
                }),
              ),
          ),
        );
      }),
    );
  }

  convertToDialog(page: PageInfo, styleSheets: StyleSheet[]): Observable<PageConversionResult> {
    if (!page.id) {
      throw new Error('Convert to dialog failed: unknown page');
    }
    if (page.pageType === 'Dialog') {
      throw new Error(`The page ${page.id} is already a Dialog.`);
    }
    return this.layoutLoadingService.getLayout(page.id).pipe(
      concatLatestFrom(() =>
        this.store
          .select(fromElements.getLayoutIds)
          .pipe(map((ids) => ids.includes(page.id as string))),
      ),
      concatMap(([layout, isLoaded]) => {
        const rootId = getRootElementId(layout.pageId);
        const entitiesState = elementsToEntityState(layout.uiLayer.content);

        const rootElement = entitiesState.entities[rootId];
        if (!rootElement) {
          throw new Error('Missing root element');
        }
        const mainChildren = isElementType(rootElement, 'root')
          ? [...(rootElement.slots.main?.children || [])]
          : isElementType(rootElement, 'drawer')
            ? [...(rootElement.slots.content?.children || [])]
            : [];

        this.convertDialogTypeMutably(
          'dialog',
          mainChildren,
          layout,
          rootElement,
          entitiesState,
          styleSheets,
        );

        return this.layoutsApi.updateLayouts([layout]).pipe(
          concatMap((layouts) =>
            this.pagesApi
              .updatePage(page.id, {
                name: page.name,
                pageType: 'Dialog',
                isHomePage: false,
                mainDatasetId: page.mainDatasetId,
                urlSlug: page.urlSlug,
              })
              .pipe(
                concatMap((dialog) => {
                  const resultActions: Action[] = [
                    fromElements.updateElementsActionPage({
                      pageId: page.id as string,
                      patch: { pageType: 'Dialog' },
                    }),
                  ];
                  if (isLoaded) {
                    const elements = layouts[0].uiLayer.content;
                    resultActions.push(fromElements.layoutsSaved({ layouts }));
                    resultActions.push(
                      fromElements.replaceElements({ ns: dialog.id as string, elements }),
                    );
                  }
                  resultActions.push(blazePageActions.convertToDialogSuccess({ updated: dialog }));
                  this.notificationService.success('Conversion complete');
                  return of({ actions: resultActions, page: dialog });
                }),
              ),
          ),
        );
      }),
    );
  }

  convertToPage(page: PageInfo): Observable<PageConversionResult> {
    if (!page.id) {
      throw new Error('Convert to page failed: unknown popup');
    }

    return this.layoutLoadingService.getLayout(page.id).pipe(
      concatLatestFrom(() =>
        this.store
          .select(fromElements.getLayoutIds)
          .pipe(map((ids) => ids.includes(page.id as string))),
      ),
      concatMap(([layout, isLoaded]) => {
        const rootId = getRootElementId(layout.pageId);
        const entitiesState = elementsToEntityState(layout.uiLayer.content);

        const modalElement = entitiesState.entities[rootId];
        if (isElementType(modalElement, 'dialog') || isElementType(modalElement, 'drawer')) {
          const contentChildren = [...(modalElement.slots.content?.children || [])];
          const footerChildren =
            'footer' in modalElement.slots ? [...(modalElement.slots.footer?.children || [])] : [];

          const form = layout.uiLayer.content.find(
            (e) => isVariantOfFormContainer(e) && e.parentNode?.id === rootId,
          );
          const formStyles = (hasProps(form) &&
            'style' in form.props &&
            form.props.style) as FormContainerPropsModel['style'];
          if (formStyles?.container?.offset?.padding === FORM_PADDING_GENERATED) {
            formStyles.container.offset.padding = FORM_PADDING_DRAGGED;
          }
          if (!form?.slots) {
            contentChildren.push(...footerChildren);
          }

          for (const elementId of [...contentChildren, ...footerChildren]) {
            removeElementFromParentMutably(entitiesState, elementId);
          }
          const initProps = { style: modalElement.props.style };
          const rootElement = this.layoutGenerationService.generateRootElement(
            layout.pageId,
            initProps,
          );
          entitiesState.entities[rootId] = rootElement;

          for (const childId of contentChildren) {
            const atPath: ElementTargetPath = {
              ...getPathLastInSlot(entitiesState, rootId, 'main'),
              targetPosition: { type: 'bottom' },
            };

            placeElementMutably(entitiesState, { elementId: childId, atPath });
          }

          if (form?.slots && hasProps(form)) {
            for (const childId of footerChildren) {
              const atPath: ElementTargetPath = {
                ...getPathLastInSlot(
                  entitiesState,
                  form.id,
                  form.slots.ids[form.slots.ids.length - 1],
                ),
                targetPosition: { type: 'bottom' },
              };

              placeElementMutably(entitiesState, {
                elementId: childId,
                atPath,
              });
            }
          }
        } else if (isElementType(modalElement, 'root')) {
          throw new Error(`The page ${layout.pageId} is already a Page.`);
        }

        layout.uiLayer.content = entityStateToElements(entitiesState);

        return this.layoutsApi.updateLayouts([layout]).pipe(
          concatMap((layouts) =>
            this.pagesApi
              .updatePage(page.id, {
                name: page.name,
                pageType: 'Page',
                isHomePage: false,
                mainDatasetId: page.mainDatasetId,
                urlSlug: page.urlSlug,
              })
              .pipe(
                concatMap((updated) => {
                  const resultActions: Action[] = [
                    fromElements.updateElementsActionPage({
                      pageId: page.id as string,
                      patch: { pageType: 'Page' },
                    }),
                  ];
                  if (isLoaded) {
                    const elements = layouts[0].uiLayer.content;
                    resultActions.push(fromElements.layoutsSaved({ layouts }));
                    resultActions.push(
                      fromElements.replaceElements({ ns: updated.id as string, elements }),
                    );
                  }
                  resultActions.push(blazePageActions.convertToPageSuccess({ updated }));
                  this.notificationService.success('Conversion complete');
                  return of({ actions: resultActions, page: updated });
                }),
              ),
          ),
        );
      }),
    );
  }

  private convertDialogTypeMutably(
    targetType: 'dialog' | 'drawer',
    mainChildren: string[],
    layout: LayoutsApiDataResponse,
    rootElement: CanvasElement,
    entitiesState: EntityState<CanvasElement>,
    styleSheets: StyleSheet[],
  ): void {
    const rootId = rootElement.id;
    const form = layout.uiLayer.content.find(
      (e) => isVariantOfFormContainer(e) && e.parentNode?.id === rootId,
    );
    const formProps = hasProps(form) ? (form.props as FormContainerPropsModel) : undefined;
    const formStyles = formProps?.style;
    if (formStyles?.container?.offset?.padding === FORM_PADDING_DRAGGED) {
      formStyles.container.offset.padding = FORM_PADDING_GENERATED;
    }

    for (const elementId of mainChildren) {
      removeElementFromParentMutably(entitiesState, elementId);
    }

    const columnsCount = formProps?.content?.options?.columnsCount;
    const initProps =
      hasProps(rootElement) && 'style' in rootElement.props
        ? { style: rootElement.props.style }
        : undefined;
    const [dialogRootElement, newBlankForm] =
      targetType === 'dialog'
        ? this.pageGenerationService.generateDefaultDialogLayout(
            layout.pageId,
            styleSheets,
            undefined,
            columnsCount,
            initProps as PartialPropsOf<'dialog'>,
          )
        : this.pageGenerationService.generateDefaultDrawerLayout(
            layout.pageId,
            styleSheets,
            undefined,
            columnsCount,
            initProps as PartialPropsOf<'drawer'>,
          );
    entitiesState.entities[rootId] = dialogRootElement;
    if (form && newBlankForm) {
      //newly created dialogRootElement has new blank form.. we need to replace it with the old one
      form.parentNode = newBlankForm.parentNode;
      dialogRootElement.slots?.ids?.forEach((id) => {
        const slot = dialogRootElement.slots?.[id];
        if (!slot?.children) {
          return;
        }
        slot.children = slot.children.map((childId) => {
          if (childId === newBlankForm.id) {
            return form.id;
          }
          return childId;
        });
      });
    }

    for (const childId of mainChildren) {
      if (childId === form?.id) {
        continue;
      }

      const atPath = getPathLastInSlot(entitiesState, rootId, 'content');
      placeElementMutably(entitiesState, { elementId: childId, atPath });
    }

    layout.uiLayer.content = entityStateToElements(entitiesState);
  }
}
